import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getLeadOutreachThreads from "../http/getLeadOutreachThreads";
import getOutreachLatestTurn from "../http/getOutreachLatestTurn";
import getOutreachTrace from "../http/getOutreachTrace";
import {
  getOutreachGmailStatus,
  postOutreachGmailCallback,
  postOutreachGmailDisconnect,
  postOutreachGmailConnect,
  postOutreachGmailDraft,
  postOutreachRefreshSent,
  OutreachGmailDraftRequest,
} from "../http/outreachGmail";
import postOutreachDraft, {
  OutreachDraftRequest,
} from "../http/postOutreachDraft";
import postOutreachPromptTest from "../http/postOutreachPromptTest";
import {
  deleteTaskPromptRule,
  getTaskPrompt,
  getTaskPromptDiff,
  reorderTaskPromptRules,
  restoreTaskPromptRevision,
  saveTaskPromptAddendum,
  saveTaskPromptPreamble,
  saveTaskPromptRule,
} from "../http/taskPrompt";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

// The outreach-email task on the unified `/leads/ai-prompt*` endpoint (mirrors the
// BE outreach.TASK_KEY). One editable rule set (there is no per-role split - the
// recipient's role is a fact in the prompt, not a separate persona).
const OUTREACH_TASK = "outreach_email";

// Draft (and re-draft) cold-outreach emails, plus the editable outreach-email prompt
// (rule set) CRUD + its test run. Mirrors the shape of useLeads / usePeopleDiscovery:
// the draft mutation holds its result in the caller's local state (the modal), so no
// cache invalidation; every prompt write invalidates the prompt query so the editor
// re-renders from the fresh active revision.
export const useOutreach = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const draftOutreach = useMutation(
    async ({ leadId, body }: { leadId: string; body: OutreachDraftRequest }) =>
      postOutreachDraft(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        body
      ),
  );
  // No onSuccess invalidation here: this mutation now resolves when the draft operation
  // is KICKED OFF, before any turn exists. The modal invalidates the thread summaries
  // when the operation completes instead.

  // Load an existing thread's latest saved turn (no draft / no AI call). Imperative, so
  // the modal can decide draft-vs-load on open and render the loaded turn just like a draft.
  const loadOutreachLatestTurn = useMutation(
    async ({
      leadId,
      outreachMessageId,
    }: {
      leadId: string;
      outreachMessageId: string;
    }) =>
      getOutreachLatestTurn(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        outreachMessageId
      )
  );

  const invalidateOutreachPrompt = {
    onSuccess: () => {
      queryClient.invalidateQueries(["outreachEmailPrompt"]);
    },
  };
  const saveOutreachPromptRuleMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptRule>[2]) =>
      saveTaskPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const deleteOutreachPromptRuleMut = useMutation(
    async (body: Parameters<typeof deleteTaskPromptRule>[2]) =>
      deleteTaskPromptRule(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const reorderOutreachPromptRulesMut = useMutation(
    async (body: Parameters<typeof reorderTaskPromptRules>[2]) =>
      reorderTaskPromptRules(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const saveOutreachPromptPreambleMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptPreamble>[2]) =>
      saveTaskPromptPreamble(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const saveOutreachPromptAddendumMut = useMutation(
    async (body: Parameters<typeof saveTaskPromptAddendum>[2]) =>
      saveTaskPromptAddendum(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );
  const restoreOutreachPromptRevisionMut = useMutation(
    async (body: Parameters<typeof restoreTaskPromptRevision>[2]) =>
      restoreTaskPromptRevision(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        body
      ),
    invalidateOutreachPrompt
  );

  const testOutreachPrompt = useMutation(
    async (body: Parameters<typeof postOutreachPromptTest>[1]) =>
      postOutreachPromptTest(await getAuthTokenBlocking(getToken, relogin), body)
  );

  return {
    draftOutreach,
    loadOutreachLatestTurn,
    saveOutreachPromptRuleMut,
    deleteOutreachPromptRuleMut,
    reorderOutreachPromptRulesMut,
    saveOutreachPromptPreambleMut,
    saveOutreachPromptAddendumMut,
    restoreOutreachPromptRevisionMut,
    testOutreachPrompt,
  };
};

// The stored AI trace for one outreach turn. `subjectRef` is the outreach message id
// (or a test run's synthetic ref); `runId` is the turn's traceRunId.
export const useOutreachTrace = (
  subjectRef: string | null,
  runId: string | null,
  enabled: boolean
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachTrace", subjectRef, runId],
    async () =>
      getOutreachTrace(
        await getAuthTokenBlocking(getToken, relogin),
        subjectRef!,
        runId!
      ),
    { enabled: !!subjectRef && !!runId && enabled }
  );
};

// Existing outreach-draft threads for a lead (per role + contact, with the turn count),
// so each contact's Draft email button can show a "v2" badge when a thread already exists.
// One batched request per lead. Re-reads on mount so a just-created draft shows its badge.
export const useLeadOutreachThreads = (leadId: string, enabled: boolean) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadOutreachThreads", leadId],
    async () =>
      getLeadOutreachThreads(
        await getAuthTokenBlocking(getToken, relogin),
        leadId
      ),
    { enabled, staleTime: 0, refetchOnMount: "always" }
  );
};

// The editable outreach-email prompt: active revision + history + enforced footer.
// Admin-only (inrange-org gated).
export const useOutreachEmailPrompt = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachEmailPrompt"],
    async () =>
      getTaskPrompt(await getAuthTokenBlocking(getToken, relogin), OUTREACH_TASK),
    { enabled }
  );
};

// Rule-level + text diff between two revisions of the outreach-email prompt.
export const useOutreachEmailPromptDiff = (
  fromRevision: number | null,
  toRevision: number | null,
  enabled = true
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachEmailPromptDiff", fromRevision, toRevision],
    async () =>
      getTaskPromptDiff(
        await getAuthTokenBlocking(getToken, relogin),
        OUTREACH_TASK,
        fromRevision!,
        toRevision!
      ),
    { enabled: enabled && fromRevision !== null && toRevision !== null }
  );
};

// Is this environment able to place drafts, and whose mailbox do they land in.
// Cheap (no Google round-trip), so the modal can read it to decide whether to offer
// the button at all.
//
// Polls only while in error: the promote gate fails closed on an unreadable status,
// so without this a transient failure would block promoting until a reload.
const GMAIL_STATUS_ERROR_RETRY_MS = 15_000;
export const useOutreachGmailStatus = (enabled = true) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["outreachGmailStatus"],
    async () =>
      getOutreachGmailStatus(await getAuthTokenBlocking(getToken, relogin)),
    {
      enabled,
      refetchInterval: (_data, query) =>
        query.state.status === "error" ? GMAIL_STATUS_ERROR_RETRY_MS : false,
    }
  );
};

// Placing a draft in the sender's Gmail, and the one-time connect that enables it.
export const useOutreachGmail = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const placeGmailDraft = useMutation(
    async ({
      leadId,
      outreachMessageId,
      body,
    }: {
      leadId: string;
      outreachMessageId: string;
      body: OutreachGmailDraftRequest;
    }) =>
      postOutreachGmailDraft(
        await getAuthTokenBlocking(getToken, relogin),
        leadId,
        outreachMessageId,
        body
      ),
    {
      onSuccess: (data, variables) => {
        // Both outcomes mean our view of this contact was stale: a placement
        // changes the thread's draft state, and "already_sent" means it was
        // emailed without the page knowing. The per-contact tags and "v{n}" badge
        // read the pipeline poll first and the thread query as a fallback, so
        // both have to re-read. The exact poll key rather than the ["leads"]
        // prefix, which would also refetch the whole inbox.
        if (data.status === "placed" || data.status === "already_sent") {
          queryClient.invalidateQueries([
            "leadOutreachThreads",
            variables.leadId,
          ]);
          queryClient.invalidateQueries(["leads", "pipelineStatus"]);
        }
      },
    }
  );

  const connectGmail = useMutation(
    async (body: { redirectUri: string; loginHint?: string | null }) =>
      postOutreachGmailConnect(
        await getAuthTokenBlocking(getToken, relogin),
        body
      )
  );

  const completeGmailConnect = useMutation(
    async (body: {
      code: string;
      state: string;
      redirectUri: string;
      userEmail?: string | null;
    }) =>
      postOutreachGmailCallback(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    {
      // The variables are an OAuth authorisation code and its state, so the
      // app's Sentry error hook must not attach them (see admin-app queryClient).
      meta: { sentryOmitVariables: true },
      onSuccess: () => {
        queryClient.invalidateQueries(["outreachGmailStatus"]);
      },
    }
  );

  // Hand the mailbox back: credential blanked, row dropped, and access revoked at
  // Google - or a `warning` in the response when that revocation can't be confirmed.
  // Invalidates the same status key the connect does, so every surface that keys
  // off "is a mailbox connected" (the header prompt, the promote gate, the drafting
  // panel) flips together rather than one at a time.
  const disconnectGmail = useMutation(
    async () =>
      postOutreachGmailDisconnect(await getAuthTokenBlocking(getToken, relogin)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["outreachGmailStatus"]);
      },
    }
  );

  // Resolve which placed drafts have since been emailed. Deliberately a separate
  // call from the leads list rather than work inside it: a HubSpot or Gmail problem
  // then degrades to "send status not refreshed this load" instead of a slow or
  // broken leads table.
  const refreshSentStatus = useMutation(
    async (leadIds: string[]) =>
      postOutreachRefreshSent(
        await getAuthTokenBlocking(getToken, relogin),
        leadIds
      ),
    {
      onSuccess: (data) => {
        if (data.sent > 0) {
          // Repaints the Status column, whose key ["leads", "pipelineStatus"] sits
          // under this prefix.
          queryClient.invalidateQueries(["leads"]);
          // The per-contact "Email sent" tag reads a DIFFERENT key, which is not
          // under ["leads"] - without this line the column flips to the sent
          // wording while the contact box beside it still says "Email drafted",
          // which is exactly the surfaces-must-agree failure this feature is
          // supposed to be on the right side of. Prefix form because a refresh
          // resolves many leads at once.
          queryClient.invalidateQueries(["leadOutreachThreads"]);
        }
      },
    }
  );

  return {
    placeGmailDraft,
    connectGmail,
    completeGmailConnect,
    disconnectGmail,
    refreshSentStatus,
  };
};

export default useOutreach;
